import { ChevronDown, ChevronUp, PanelLeftOpen } from "lucide-react";
import type { TriageItemRow } from "@/hooks/use-triage";
import { cn } from "@/lib/utils";
import { TRIAGE_DOT } from "./triage-tones";

interface ReviewMiniRailProps {
  items: TriageItemRow[];
  selectedItemId: string | null;
  onSelect: (item: TriageItemRow) => void;
  onPrev: () => void;
  onNext: () => void;
  onExpand: () => void;
}

export function ReviewMiniRail({
  items,
  selectedItemId,
  onSelect,
  onPrev,
  onNext,
  onExpand,
}: ReviewMiniRailProps) {
  const idx = selectedItemId ? items.findIndex((i) => i.id === selectedItemId) : -1;
  const cur = idx >= 0 ? idx + 1 : 0;
  const total = items.length;

  return (
    <aside className="flex flex-col items-center gap-3 py-3 px-1.5 bg-card border-r border-[var(--color-border)] h-full overflow-hidden">
      <button
        type="button"
        onClick={onExpand}
        title="Show queue"
        className="group relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--color-border)] bg-card text-foreground/70 hover:bg-nymbl-sky hover:border-nymbl-navy hover:text-nymbl-navy transition-colors"
      >
        <PanelLeftOpen className="h-4 w-4" />
      </button>

      <div className="flex flex-col items-center gap-1 py-2 border-y border-[var(--color-border)] w-full">
        <button
          type="button"
          onClick={onPrev}
          disabled={idx <= 0}
          title="Previous (k or ↑)"
          className="inline-flex h-7 w-8 items-center justify-center rounded text-foreground/70 hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <div className="flex flex-col items-center font-mono leading-tight">
          <span className="text-[12px] font-semibold text-nymbl-navy tabular-nums">
            {cur || "—"}
          </span>
          <span className="text-[10px] text-muted-foreground/70">/</span>
          <span className="text-[10px] text-muted-foreground tabular-nums">{total}</span>
        </div>
        <button
          type="button"
          onClick={onNext}
          disabled={idx < 0 || idx >= total - 1}
          title="Next (j or ↓)"
          className="inline-flex h-7 w-8 items-center justify-center rounded text-foreground/70 hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 w-full overflow-y-auto overflow-x-hidden flex flex-col items-center gap-[3px] py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item) => {
          const selected = item.id === selectedItemId;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item)}
              title={`${item.externalKey ?? item.id.slice(0, 6)} — ${item.extracted.slice(0, 80)}`}
              className={cn(
                "shrink-0 transition-transform hover:scale-[1.4]",
                selected
                  ? "h-[22px] w-1 rounded-sm bg-nymbl-navy"
                  : cn(
                      "h-2 w-2 rounded-full",
                      TRIAGE_DOT[item.decision ? item.effectiveStatus : "pending"],
                    ),
              )}
            />
          );
        })}
      </div>
    </aside>
  );
}
